import { ButtonType, Step } from "react-joyride";
import {
  ADDITIONAL_RECIPIENTS_STEP,
  EMAIL_SETTINGS_STEP,
  MAIN_RECIPIENT_STEP,
  MAP_ADDRESS_STEP,
  MAP_CHANGE_ADDRESS_STEP,
  MAP_CONFIRM_STEP,
  MAP_OVERWRITE_ADDRESS_STEP,
  MAP_PIN_STEP,
  PROPOSAL_FORMAT_STEP,
  SELECT_PRODUCT_STEP,
} from "modules/proposals/draft/draftTourContent";

export const WAIT_FOR_USER = {
  buttons: ["close"] as ButtonType[],
  dismissKeyAction: false as const,
};

export const LOOK_ONLY = {
  blockTargetInteraction: true,
};

export const NO_BACK = {
  buttons: ["close", "primary"] as ButtonType[],
};

export const waitFor = (selector: string, timeoutMs = 3000): Promise<void> =>
  new Promise((resolve) => {
    if (document.querySelector(selector)) {
      resolve();
      return;
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        resolve();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      resolve();
    }, timeoutMs);
  });

export const waitForSettled = async (
  selector: string,
  timeoutMs = 3000,
  stableFramesNeeded = 3
): Promise<void> => {
  await waitFor(selector, timeoutMs);
  if (!document.querySelector(selector)) return;

  const deadline = Date.now() + timeoutMs;
  let previous = document.querySelector(selector)?.getBoundingClientRect();
  let stableFrames = 0;
  while (stableFrames < stableFramesNeeded && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => requestAnimationFrame(resolve));
    // Queried every frame: the element can be re-rendered while it animates (e.g. the map pin).
    const rect = document.querySelector(selector)?.getBoundingClientRect();
    const moved =
      !rect ||
      !previous ||
      rect.top !== previous.top ||
      rect.left !== previous.left ||
      rect.width !== previous.width ||
      rect.height !== previous.height;
    stableFrames = moved ? 0 : stableFrames + 1;
    previous = rect;
  }
};

export const clickElement = (selector: string) => {
  (document.querySelector(selector) as HTMLElement | null)?.click();
};

type Variant = { target: string; content: string };

function VariantContent({ when, a, b }: { when: () => boolean; a: string; b: string }) {
  return <>{when() ? a : b}</>;
}

export const variantStep = (
  id: string,
  when: () => boolean,
  whenTrue: Variant,
  whenFalse: Variant,
  options: Partial<Step> = {}
): Step => ({
  id,
  target: () => document.querySelector<HTMLElement>(when() ? whenTrue.target : whenFalse.target),
  content: <VariantContent when={when} a={whenTrue.content} b={whenFalse.content} />,
  ...options,
});

export const withTourDefaults = (steps: Step[]): Step[] =>
  steps.map((step) => ({ overlayClickAction: false, ...step }));

export const mapSteps = (openMap: () => Promise<void>): Step[] => [
  {
    id: "map-address",
    ...MAP_ADDRESS_STEP,
    before: openMap,
    targetWaitTimeout: 5000,
    ...WAIT_FOR_USER,
  },
  {
    id: "map-pin",
    ...MAP_PIN_STEP,
    // The map pans and zooms to the address while the pin is dropped; wait until it stops.
    before: () => waitForSettled(MAP_PIN_STEP.target as string, 5000, 10),
    targetWaitTimeout: 5000,
    ...NO_BACK,
  },
  {
    id: "map-change-address",
    ...MAP_CHANGE_ADDRESS_STEP,
    ...LOOK_ONLY,
  },
  {
    id: "map-overwrite-address",
    ...MAP_OVERWRITE_ADDRESS_STEP,
    ...LOOK_ONLY,
  },
  {
    id: "map-confirm",
    ...MAP_CONFIRM_STEP,
    ...WAIT_FOR_USER,
  },
];

export const jobContinueStep: Step = {
  id: "job-continue",
  target: ".tour-job-continue",
  content: "Click Continue to save your job and add products.",
  targetWaitTimeout: 3000,
  ...WAIT_FOR_USER,
};

export const selectProductStep: Step = {
  id: "select-product",
  ...SELECT_PRODUCT_STEP,
  before: () => waitForSettled(".tour-select-product", 5000),
  targetWaitTimeout: 5000,
  placement: "top",
  ...NO_BACK,
};

const isTruckingProduct = () => !!document.querySelector(".tour-material-trucking");

const materialStep = (id: string, plain: Variant, trucking: Variant): Step =>
  variantStep(id, isTruckingProduct, trucking, plain, LOOK_ONLY);

export const productSectionSteps = (measureJob: Partial<Step>): Step[] => [
  {
    id: "measure-job",
    target: ".tour-measure-job",
    content: "You may choose to measure your job size on our integrated Maps feature.",
    ...LOOK_ONLY,
    ...NO_BACK,
    ...measureJob,
  },
  {
    id: "job-size",
    target: ".tour-job-size",
    content: "Or type in a known job size here.",
    ...LOOK_ONLY,
  },
  {
    id: "profit",
    target: ".tour-profit-markup",
    content: "Your profit percentage, designated from your product settings.",
    ...LOOK_ONLY,
  },
  {
    id: "production",
    target: ".tour-production-fields",
    content:
      "This is your default production rate which is used to calculate expected job days and hours. You may overwrite these numbers as needed.",
    ...LOOK_ONLY,
  },
  {
    id: "travel-time",
    target: ".tour-travel-time",
    content: "This is the round trip travel time to and from the job.",
    ...LOOK_ONLY,
  },
  materialStep(
    "material-window",
    { target: ".tour-material-costs", content: "Now you may fine-tune your materials." },
    {
      target: ".tour-material-trucking",
      content:
        "Now you may fine-tune your materials and trucking. Use the ? icon here any time for a detailed walkthrough of trucking.",
    }
  ),
  materialStep(
    "material-add",
    {
      target: ".tour-material-costs .tour-cost-add",
      content: "Clicking here allows you to quickly add, and adjust, new materials.",
    },
    {
      target: ".tour-material-trucking-add",
      content: "Clicking here allows you to quickly add, and adjust, new materials.",
    }
  ),
  materialStep(
    "material-configure",
    {
      target: ".tour-material-costs .tour-cost-action",
      content: "Click here to view and adjust the application rate for your product.",
    },
    {
      target: ".tour-configure-trucking",
      content: "Click here to view and edit your Trucking Configuration.",
    }
  ),
  materialStep(
    "material-delete",
    {
      target: ".tour-material-costs .tour-cost-delete",
      content: "Clicking here immediately removes the associated item.",
    },
    {
      target: ".tour-plant-product-delete",
      content: "Clicking here immediately removes the associated item.",
    }
  ),
  {
    id: "equipment-costs",
    target: ".tour-equipment-costs",
    content:
      "Here you see the equipment associated with your job, which you can edit as necessary.",
    ...LOOK_ONLY,
  },
  {
    id: "labor-costs",
    target: ".tour-labor-costs",
    content: "View and edit labor costs.",
    ...LOOK_ONLY,
  },
  {
    id: "additional-costs",
    target: ".tour-additional-costs",
    content:
      "Add any additional costs that are not generally included in your basic job description.",
    ...LOOK_ONLY,
  },
  {
    id: "product-files",
    target: ".tour-product-files",
    content:
      "Here you can add files, such as job site pictures, that will be available to view through the proposal, schedule, and in the Bitumio App.",
    ...LOOK_ONLY,
  },
  {
    id: "add-job",
    target: ".tour-add-job-button",
    content: "You can add and estimate multiple Jobs within one Proposal.",
    ...LOOK_ONLY,
  },
];

export const totalsAndSendSteps: Step[] = [
  {
    id: "totals-costs",
    target: ".tour-totals-costs",
    content: "With your proposal completed, you can now view a breakdown of your expenditures.",
    ...LOOK_ONLY,
  },
  {
    id: "totals-profit",
    target: ".tour-totals-profit",
    content: "And here is your expected profits, including any discounts or taxes you've added.",
    ...LOOK_ONLY,
  },
  {
    id: "preview-proposal",
    target: ".tour-preview-proposal",
    content: "Click here to see a preview of your proposal before sending.",
    ...LOOK_ONLY,
  },
  {
    id: "send-proposal",
    target: ".tour-send-proposal",
    content: "Click here to confirm your proposal details before sending.",
    ...WAIT_FOR_USER,
    buttons: ["back", "close"],
  },
  {
    id: "proposal-format",
    ...PROPOSAL_FORMAT_STEP,
    before: () => waitForSettled(".tour-proposal-format", 8000),
    targetWaitTimeout: 8000,
    placement: "top",
    ...NO_BACK,
  },
  {
    id: "main-recipient",
    ...MAIN_RECIPIENT_STEP,
  },
  {
    id: "additional-recipients",
    ...ADDITIONAL_RECIPIENTS_STEP,
  },
  {
    id: "email-settings",
    ...EMAIL_SETTINGS_STEP,
  },
  {
    id: "send-proposal-button",
    target: ".tour-send-proposal-button",
    content: "This is the button you click to send the proposal to the customer.",
    locale: { last: "Finish" },
  },
];
